import { SEARCH_DEBOUNCE_MS, SEARCH_PLACEHOLDERS } from '../config';
import { categoryCounts, topTags } from '../filters';
import { CATEGORY_META, metaFor } from '../data/categories';
import type { AppState, Entry, EntryType, SortKey } from '../types';
import type { ViewMode } from './rows';
import { createDropdown } from './dropdown';
import { iconSvg } from './icon';
import { browserCaps, sharePage } from '../share';
import { escapeHtml } from './util';

/**
 * Size of every icon in the masthead. One constant because the cluster reads as a set — the push
 * bell is injected separately by src/push.ts and matches this deliberately, so it does not turn up
 * a size smaller than the icons either side of it.
 */
export const HEADER_ICON = 17;

export interface HeaderCallbacks {
  onSearch: (q: string) => void;
  onType: (type: EntryType) => void;
  /** Toggle a single category in the multi-select filter. */
  onCategory: (category: string) => void;
  onTag: (tag: string) => void;
  onSort: (sort: SortKey) => void;
  onViewToggle: () => void;
  onSurprise: () => void;
  onThemeToggle: () => void;
}

/** Extra context the header's mobile controls need beyond state + view. */
export interface HeaderContext {
  entries: Entry[];
  /** Weekly analytics exist — gates the Trending/Popular sorts. */
  hasWeekly: boolean;
}

export interface HeaderHandle {
  update: (state: AppState, view: ViewMode, ctx: HeaderContext) => void;
  focusSearch: () => void;
}

/** The view swap shows the view you'll switch TO. */
function viewSwapInner(view: ViewMode): string {
  return iconSvg(view === 'rows' ? 'layout-grid' : 'list', HEADER_ICON);
}

function viewSwapTitle(view: ViewMode): string {
  return view === 'rows' ? 'Card view' : 'List view';
}

const TYPE_LABELS: [EntryType, string][] = [
  ['all', 'All'],
  ['tool', 'Tools'],
  ['web', 'Sites'],
  ['game', 'Games'],
  ['product', 'Products'],
];

function typeLabel(type: EntryType): string {
  return TYPE_LABELS.find(([t]) => t === type)?.[1] ?? 'All';
}

// Ordered sort options. Trending/Popular are dropped when there's no weekly
// analytics yet (mirrors the disabled column headers on desktop).
const SORT_LABELS: [SortKey, string][] = [
  ['new', 'Newest'],
  ['trending', 'Trending'],
  ['popular', 'Popular'],
  ['az', 'A–Z'],
];

function sortLabel(sort: SortKey): string {
  return SORT_LABELS.find(([s]) => s === sort)?.[1] ?? 'Newest';
}

/** Ordered category values present in the current pool: known meta order, then extras. */
function categoryList(counts: Map<string, number>): string[] {
  const known = Object.keys(CATEGORY_META).filter((c) => (counts.get(c) ?? 0) > 0);
  const extras = [...counts.keys()].filter((c) => !CATEGORY_META[c]);
  return [...known, ...extras];
}

function categoryTriggerLabel(categories: string[]): string {
  if (categories.length === 0) return 'Category';
  if (categories.length === 1) return metaFor(categories[0]).label;
  return `Categories · ${categories.length}`;
}

export function renderHeader(
  container: HTMLElement,
  state: AppState,
  view: ViewMode,
  entries: Entry[],
  cb: HeaderCallbacks,
): HeaderHandle {
  container.innerHTML = `
    <div class="header-inner">
      <a class="wordmark" href="/">lab<span class="wordmark-dot">.</span></a>
      <div class="search-wrap">
        <span class="search-icon" aria-hidden="true">${iconSvg('search', HEADER_ICON)}</span>
        <input id="search" type="search" autocomplete="off" spellcheck="false"
          placeholder="${escapeHtml(SEARCH_PLACEHOLDERS[0])}"
          aria-label="Search tools and sites" value="${escapeHtml(state.q)}" />
        <span class="search-kbd" aria-hidden="true">/</span>
        <button type="button" class="search-close" aria-label="Close search">${iconSvg('x', HEADER_ICON)}</button>
      </div>
      <nav class="header-controls">
        <div class="type-links" role="group" aria-label="Filter by type">
          ${TYPE_LABELS.map(
            ([t, label]) =>
              `<button class="text-link type-link" data-type="${t}" aria-pressed="${state.type === t}">${label}</button>`,
          ).join('')}
        </div>
        <button id="search-toggle" class="text-link text-link-quiet theme-toggle hdr-search-toggle" title="Search" aria-label="Search" aria-expanded="false">${iconSvg('search', HEADER_ICON)}</button>
        <button id="surprise" class="text-link text-link-quiet theme-toggle" title="Open a random entry" aria-label="Open a random entry">${iconSvg('dices', HEADER_ICON)}</button>
        <button id="share" class="text-link text-link-quiet theme-toggle" title="Share this page" aria-label="Share this page">${iconSvg('share', HEADER_ICON)}</button>
        <button id="view-swap" class="text-link text-link-quiet theme-toggle" title="${viewSwapTitle(view)}" aria-label="Switch view">${viewSwapInner(view)}</button>
        <button id="theme-toggle" class="text-link text-link-quiet theme-toggle" title="Toggle theme" aria-label="Toggle light/dark theme">${iconSvg('sun-moon', HEADER_ICON)}</button>
        <a class="text-link text-link-quiet theme-toggle" href="/about.html" title="About" aria-label="About">${iconSvg('circle-help', HEADER_ICON)}</a>
      </nav>
    </div>
    <div class="header-filters"></div>`;

  const search = container.querySelector<HTMLInputElement>('#search')!;
  const typeButtons = [...container.querySelectorAll<HTMLButtonElement>('.type-link')];
  const searchToggle = container.querySelector<HTMLButtonElement>('#search-toggle')!;
  const searchClose = container.querySelector<HTMLButtonElement>('.search-close')!;
  const filtersRow = container.querySelector<HTMLElement>('.header-filters')!;

  // Mobile filter dropdowns (styled, non-native). Hidden on desktop via CSS,
  // where the inline type-links + the filter-index tag line take over.
  const typeDropdown = createDropdown({
    ariaLabel: 'Filter by type',
    className: 'dd-type',
    onSelect: (v) => cb.onType(v as EntryType),
  });
  const categoryDropdown = createDropdown({
    ariaLabel: 'Filter by category',
    className: 'dd-cat',
    multi: true,
    onSelect: (v) => cb.onCategory(v),
  });
  const sortDropdown = createDropdown({
    ariaLabel: 'Sort',
    className: 'dd-sort',
    onSelect: (v) => cb.onSort(v as SortKey),
  });
  const tagsDropdown = createDropdown({
    ariaLabel: 'Filter by tag',
    className: 'dd-tags',
    multi: true,
    onSelect: (v) => cb.onTag(v),
  });
  filtersRow.append(typeDropdown.el, categoryDropdown.el, sortDropdown.el, tagsDropdown.el);

  const openSearch = (): void => {
    // A dropdown left open would be hidden (not closed) by the search overlay,
    // leaking its document listeners — close them first.
    typeDropdown.close();
    categoryDropdown.close();
    sortDropdown.close();
    tagsDropdown.close();
    container.classList.add('searching');
    searchToggle.setAttribute('aria-expanded', 'true');
    search.focus();
  };
  const closeSearch = (): void => {
    container.classList.remove('searching');
    searchToggle.setAttribute('aria-expanded', 'false');
    // The input is about to be display:none'd — return focus to the toggle so
    // keyboard/AT users aren't dropped to <body>.
    searchToggle.focus();
  };
  searchToggle.addEventListener('click', openSearch);
  searchClose.addEventListener('click', closeSearch);

  let debounce: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener('input', () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => cb.onSearch(search.value), SEARCH_DEBOUNCE_MS);
  });
  search.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (search.value) {
        search.value = '';
        cb.onSearch('');
      } else {
        closeSearch();
      }
    }
  });

  typeButtons.forEach((btn) =>
    btn.addEventListener('click', () => cb.onType(btn.dataset.type as EntryType)),
  );
  container.querySelector('#surprise')!.addEventListener('click', cb.onSurprise);

  /**
   * Share the page you are actually looking at. Every filter and the open entry live in the query
   * string, so `location.href` is already the shareable thing — no state needs threading in here.
   *
   * `sharePage` is called straight out of the click with no `await` before it: `navigator.share`
   * requires user activation, and activation does not survive an intervening await in Safari.
   */
  const shareBtn = container.querySelector<HTMLButtonElement>('#share')!;
  let shareReset: ReturnType<typeof setTimeout> | undefined;
  shareBtn.addEventListener('click', () => {
    void sharePage(location.href, document.title, browserCaps()).then((outcome) => {
      if (outcome === 'shared' || outcome === 'dismissed') return;
      // No share sheet, so say what DID happen — a button that appears to do nothing is worse
      // than one that admits it only copied a link.
      clearTimeout(shareReset);
      const ok = outcome === 'copied';
      shareBtn.innerHTML = iconSvg(ok ? 'check' : 'x', HEADER_ICON);
      shareBtn.classList.toggle('hdr-share--done', ok);
      shareBtn.title = ok ? 'Link copied' : 'Could not share';
      shareReset = setTimeout(() => {
        shareBtn.innerHTML = iconSvg('share', HEADER_ICON);
        shareBtn.classList.remove('hdr-share--done');
        shareBtn.title = 'Share this page';
      }, 1600);
    });
  });

  const viewSwap = container.querySelector<HTMLButtonElement>('#view-swap')!;
  viewSwap.addEventListener('click', cb.onViewToggle);
  container.querySelector('#theme-toggle')!.addEventListener('click', cb.onThemeToggle);

  let phIdx = 0;
  setInterval(() => {
    if (!search.value && document.activeElement !== search) {
      phIdx = (phIdx + 1) % SEARCH_PLACEHOLDERS.length;
      search.placeholder = SEARCH_PLACEHOLDERS[phIdx];
    }
  }, 4000);

  const update = (s: AppState, v: ViewMode, ctx: HeaderContext) => {
    if (search.value !== s.q) search.value = s.q;
    typeButtons.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.type === s.type)));
    viewSwap.innerHTML = viewSwapInner(v);
    viewSwap.title = viewSwapTitle(v);
    searchToggle.classList.toggle('hdr-search-toggle--active', s.q.trim().length > 0);

    typeDropdown.update({
      options: TYPE_LABELS.map(([value, label]) => ({ value, label })),
      selected: [s.type],
      triggerLabel: typeLabel(s.type),
      active: s.type !== 'all',
    });

    const counts = categoryCounts(s, ctx.entries);
    categoryDropdown.update({
      options: categoryList(counts).map((c) => ({
        value: c,
        label: metaFor(c).label,
        count: counts.get(c) ?? 0,
      })),
      selected: s.categories,
      triggerLabel: categoryTriggerLabel(s.categories),
      active: s.categories.length > 0,
    });

    const sorts = SORT_LABELS.filter(
      ([sk]) => ctx.hasWeekly || (sk !== 'trending' && sk !== 'popular'),
    );
    sortDropdown.update({
      options: sorts.map(([value, label]) => ({ value, label })),
      selected: [s.sort],
      triggerLabel: sortLabel(s.sort),
      active: s.sort !== 'new',
    });

    const tags = topTags(s, ctx.entries);
    // Close (not just hide) so an open tags panel doesn't leak its listeners
    // when the category is cleared.
    if (tags.length === 0) tagsDropdown.close();
    tagsDropdown.el.hidden = tags.length === 0;
    tagsDropdown.update({
      options: tags.map((t) => ({ value: t, label: `#${t}` })),
      selected: s.tags,
      triggerLabel: s.tags.length ? `Tags · ${s.tags.length}` : 'Tags',
      active: s.tags.length > 0,
    });
  };

  update(state, view, { entries, hasWeekly: false });

  return {
    update,
    focusSearch: openSearch,
  };
}
